import { mergeAttributes } from "@tiptap/core";

import { twMerge } from "@/utils/twMerge";

import {
  collapseRuleSpec,
  collapsedNumberSlotSpec,
  toggleButtonSpec,
  toggleSectionButtonSpec,
  togglePillSpec,
} from "./specs/collapse";
import { lineNumberSpec } from "./specs/lineNumber";
import { lockIndicatorSpec } from "./specs/lockIndicator";

import type { Translator } from "@/components/tiptap/EditorTranslator";
import type { CollapseType } from "@corca/tiptap";
import type { DOMOutputSpec } from "@tiptap/pm/model";

export type Props = Partial<{
  className: string;
  wrapperClassName: string;
  contentClassName: string;
  numberClassName: string;
  label: string;
  showLineNumber?: boolean;
  showLineOptionButtons?: boolean;
  collapsed?: boolean;
  collapseType?: CollapseType;
  locked?: boolean;
  /**
   * Top-level node type name, used in the collapse toggle's aria-label
   * ("Collapse Paragraph", "Collapse Callout").
   */
  topLevelNodeTypeName?: string;
  t?: Translator;
}>;

export function renderLineWrapperHTML(
  content?: DOMOutputSpec,
  {
    className,
    wrapperClassName,
    contentClassName,
    numberClassName,
    label,
    showLineNumber,
    showLineOptionButtons,
    collapsed,
    collapseType,
    locked,
    topLevelNodeTypeName,
    t,
  }: Props = {},
): DOMOutputSpec {
  const contentSpec: DOMOutputSpec = content ?? ["div"];
  const isTopLevel = Boolean(showLineNumber || showLineOptionButtons);

  if (collapsed && collapseType === "block") {
    const collapsedInner: DOMOutputSpec = [
      "div",
      { class: "group/line flex flex-1 items-center min-w-0 sm:max-w-full" },
      collapsedNumberSlotSpec(topLevelNodeTypeName, t),
      collapseRuleSpec,
    ];

    return [
      "div",
      {
        class: twMerge(
          "line-wrapper is-collapsed group/line relative max-w-content-width w-full mx-auto sm:max-w-full cursor-pointer",
          className,
          "sm:-my-1.5 fromSm:-my-3.5",
        ),
      },
      [
        "div",
        mergeAttributes(
          {
            class: twMerge(
              "flex flex-1 items-center cursor-pointer sm:-mx-[6px] fromSm:-mx-2",
              wrapperClassName,
            ),
          },
          {
            "aria-label": label,
            contenteditable: "false",
          },
        ),
        collapsedInner,
      ],
    ];
  }

  const headingToggle = collapseType === "section" && topLevelNodeTypeName === "heading";

  let gutterToggle: DOMOutputSpec | null = null;

  if (collapseType === "block") {
    gutterToggle = toggleButtonSpec(!!collapsed, {
      nodeTypeName: topLevelNodeTypeName,
      className: "opacity-0 group-hover/line:opacity-100",
      t,
    });
  } else if (collapseType === "section" && !headingToggle) {
    gutterToggle = toggleSectionButtonSpec(!!collapsed, { nodeTypeName: topLevelNodeTypeName, t });
  }

  const reserveGutterToggle = showLineNumber && !headingToggle;
  const numberSlotChildren: DOMOutputSpec[] = [];

  if (locked && showLineNumber) {
    numberSlotChildren.push(lockIndicatorSpec(t));
  }

  if (showLineNumber) {
    numberSlotChildren.push(lineNumberSpec);
  }

  if (reserveGutterToggle) {
    numberSlotChildren.push([
      "div",
      { class: "flex shrink-0 items-center justify-center w-7 sm:hidden" },
      ...(gutterToggle ? [gutterToggle] : []),
    ]);
  }

  const renderedContent = headingToggle
    ? togglePillSpec(contentSpec, !!collapsed, { nodeTypeName: topLevelNodeTypeName, t })
    : contentSpec;

  const wrapperAttributes = mergeAttributes(
    {
      class: twMerge(
        "flex flex-1 items-center cursor-text",
        showLineNumber && "my-2.5",
        showLineOptionButtons && "relative -mr-12 pr-12 sm:mr-0 sm:pr-0",
        wrapperClassName,
      ),
    },
    {
      "aria-label": label,
    },
  );

  const lineContent: DOMOutputSpec[] = [
    [
      "div",
      {
        class: twMerge(
          "relative flex items-center",
          // TODO: dynamic line numbers width
          // https://linear.app/corca-research/issue/D-2987/dynamic-line-numbers-column-width
          showLineNumber && "fromSm:-ml-7",
          numberClassName,
        ),
        contenteditable: "false",
        // The block menu has the gutter's actions, without reading them out on every line.
        "aria-hidden": "true",
      },
      ...numberSlotChildren,
    ],
    [
      "div",
      {
        class: twMerge(
          // Keep actual line content above the line hover background (the wrapper ::before
          // and the overlay-mode portal background).
          "relative z-10 flex flex-1 items-center flex-nowrap min-w-0 sm:flex-wrap sm:w-full sm:max-w-full sm:overflow-hidden",
          headingToggle &&
            "fromSm:pl-5.5 fromSm:-indent-5.5 fromSm:ml-1 fromSm:mb-[0.2em] fromSm:translate-y-[0.2em]",
          contentClassName,
        ),
      },
      renderedContent,
    ],
  ];

  const inner: DOMOutputSpec = [
    "div",
    { class: "group/line flex flex-1 items-baseline min-w-0 sm:max-w-full" },
    ...lineContent,
  ];

  const wrapper: DOMOutputSpec = ["div", wrapperAttributes, inner];

  return [
    "div",
    {
      class: twMerge(
        "line-wrapper relative max-w-content-width w-full mx-auto sm:max-w-full",
        isTopLevel &&
          "before:content-[''] sm:before:hidden before:absolute before:left-11.5 before:top-0 before:bottom-0 before:right-0 before:rounded-10 before:-my-1.25 before:pointer-events-none",
        className,
      ),
    },
    wrapper,
  ];
}
